import { useCallback, useRef, useState } from "react";

export const useDlqFileValidation = () => {
  const [file, setFile] = useState<File>();
  const [messageCount, setMessageCount] = useState<number>(0);
  const [error, setError] = useState<string>("");
  const [validating, setValidating] = useState(false);
  const [redriveOK, setRedriveOK] = useState(false);
  const validationSequence = useRef(0);

  const validateFile = useCallback(
    async (selectedFile: File): Promise<number> => {
      const text = await selectedFile.text();
      if (!text.trim()) {
        throw new Error("File is empty");
      }
      const lines = text.trim().split("\n");

      const headers = lines[0]
        .split(",")
        .map((h) => h.trim().replace(/^"|"$/g, ""));
      if (!headers.includes("dlq_pk")) {
        throw new Error("File must have 'dlq_pk' as a CSV header");
      }

      const pks = lines.slice(1).filter((line) => line.trim());
      if (pks.length === 0) {
        throw new Error("File must contain at least one DLQ pk");
      }
      return pks.length;
    },
    []
  );

  const handleFileChange = useCallback(
    async (files: File[]): Promise<void> => {
      const validationId = validationSequence.current + 1;
      validationSequence.current = validationId;
      const selectedFile = files[0];

      setFile(selectedFile);
      setMessageCount(0);
      setError("");
      setRedriveOK(false);

      if (!selectedFile) {
        setValidating(false);
        return;
      }

      setValidating(true);
      try {
        const count = await validateFile(selectedFile);
        if (validationSequence.current !== validationId) {
          return;
        }
        setMessageCount(count);
        setRedriveOK(true);
      } catch (err) {
        if (validationSequence.current !== validationId) {
          return;
        }
        setMessageCount(0);
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (validationSequence.current === validationId) {
          setValidating(false);
        }
      }
    },
    [validateFile]
  );

  return {
    file,
    messageCount,
    error,
    validating,
    redriveOK,
    handleFileChange,
  };
};
